// 列表无限滚动 + 底部状态包装组件 封装 antd-mobile InfiniteScroll
// src/components/infinite-scroll-list/index.tsx

'use client';

import { useTranslations } from 'next-intl';

import { InfiniteScroll } from 'antd-mobile';

interface IFooterProps {
    /** 是否还有更多 由 antd-mobile InfiniteScroll 透传 */
    hasMore: boolean;
    /** 加载是否失败 由 antd-mobile InfiniteScroll 透传 */
    failed: boolean;
    /** 重试回调 点击文案高亮部分触发 由 antd-mobile InfiniteScroll 透传 */
    retry: () => void;
}

/** 底部三态渲染 失败重试 / 加载占位 / 无更多 仅供 InfiniteScrollList 内部使用 */
function Footer(props: IFooterProps) {
    const { hasMore, failed, retry } = props;
    const t = useTranslations('Common');

    // 加载失败：展示文案 + 内联重试链接，点击高亮部分触发 retry
    if (failed) {
        return (
            <div className='text-[12px] text-[#888]'>
                {t.rich('load_more_try_again', {
                    highlight: chunks => (
                        <span className='text-[#7039DF]' onClick={retry}>
                            {chunks}
                        </span>
                    ),
                })}
            </div>
        );
    }

    // 还有更多：展示静态三点占位 等待 antd-mobile 触发 loadMore
    if (hasMore) {
        return (
            <div className='flex items-center justify-center gap-[4px]'>
                <div className='size-[4px] rounded-full bg-[#888]' />
                <div className='size-[4px] rounded-full bg-[#888]' />
                <div className='size-[4px] rounded-full bg-[#888]' />
            </div>
        );
    }

    // 无更多数据：列表已完整加载
    return <div className='text-[12px] text-[#888]'>{t('no_more')}</div>;
}

interface IInfiniteScrollListProps {
    /**
     * 触底时触发的加载更多函数 通常透传 useInfiniteListStatus 返回的 loadMore
     * !注意：此处 load more 不需要手动 try catch，会自动在组件内部处理，否则会影响组件内部逻辑
     */
    loadMore: () => Promise<void>;
    /** 是否还有更多数据 false 时底部展示「无更多」文案 */
    hasMore: boolean;
}

/**
 * 列表无限滚动 + 底部状态
 * 封装 antd-mobile InfiniteScroll，底部按 failed > hasMore > 无更多 三态展示
 *
 * 通常配合 `useInfiniteListStatus` 使用，作为列表内容的最后一个子节点
 *
 * @example
 * <InfiniteScrollList loadMore={loadMore} hasMore={hasMore} />
 */
export default function InfiniteScrollList(props: IInfiniteScrollListProps) {
    const { loadMore, hasMore } = props;

    return (
        <InfiniteScroll loadMore={loadMore} hasMore={hasMore}>
            {(currentHasMore, failed, retry) => <Footer hasMore={currentHasMore} failed={failed} retry={retry} />}
        </InfiniteScroll>
    );
}
